import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { buildAgreementsPayloadFromRows, mapAgreementsResponseToDto } from '../dto/system-agreements.dto.js';
import { buildAgreementsRowsModel, buildNewAgreementRow, reorderAgreementRows } from '../model/system-agreements.model.js';

export function useSystemAgreements() {
  const { token } = useAuth();

  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState('');
  const [dragSourceId, setDragSourceId] = useState('');

  const loadAgreements = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.requestAdmin({
        token,
        method: 'GET',
        path: '/admin/system/user-settings/agreements',
      });

      const dto = mapAgreementsResponseToDto(response);
      setRows(buildAgreementsRowsModel(dto));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setRows([]);
      } else {
        setError(requestError.message || 'Failed to load agreements');
        setRows([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAgreements();
  }, [loadAgreements]);

  const persistRows = useCallback(
    async (nextRows, successMessage = 'Saved successfully.') => {
      setIsSaving(true);
      setError('');
      setSuccess('');

      try {
        const payload = buildAgreementsPayloadFromRows(nextRows);
        const response = await adminApi.requestAdmin({
          token,
          method: 'POST',
          path: '/admin/system/user-settings/agreements',
          body: { agreements: payload },
        });

        const dto = mapAgreementsResponseToDto(response);
        const normalizedRows = buildAgreementsRowsModel(dto);
        setRows(normalizedRows.length ? normalizedRows : buildAgreementsRowsModel(payload));
        setSuccess(successMessage);
      } catch (requestError) {
        setError(requestError.message || 'Failed to save agreements');
        await loadAgreements();
      } finally {
        setIsSaving(false);
      }
    },
    [loadAgreements, token],
  );

  const handleAdd = useCallback(async (event) => {
    event.preventDefault();
    const value = String(newName || '').trim();

    if (!value) {
      return;
    }

    const nextRows = [...rows, buildNewAgreementRow(value)];
    setRows(nextRows);
    setNewName('');
    await persistRows(nextRows, 'Agreement added.');
  }, [newName, persistRows, rows]);

  const startEdit = useCallback((id) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, isEditing: true, draftName: row.name } : row)));
  }, []);

  const cancelEdit = useCallback((id) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, isEditing: false, draftName: row.name } : row)));
  }, []);

  const updateDraft = useCallback((id, draftName) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, draftName } : row)));
  }, []);

  const saveEdit = useCallback(async (id) => {
    const nextRows = rows.map((row) => {
      if (row.id !== id) {
        return row;
      }

      const nextName = String(row.draftName || '').trim();
      return {
        ...row,
        name: nextName || row.name,
        draftName: nextName || row.name,
        isEditing: false,
      };
    });

    setRows(nextRows);
    await persistRows(nextRows, 'Agreement updated.');
  }, [persistRows, rows]);

  const requestDeleteRow = useCallback((id) => {
    setDeleteTargetId(id);
  }, []);

  const cancelDeleteRow = useCallback(() => {
    setDeleteTargetId('');
  }, []);

  const confirmDeleteRow = useCallback(async () => {
    if (!deleteTargetId) {
      return;
    }

    const nextRows = rows.filter((row) => row.id !== deleteTargetId);
    setRows(nextRows);
    setDeleteTargetId('');
    await persistRows(nextRows, 'Agreement deleted.');
  }, [deleteTargetId, persistRows, rows]);

  const handleDragStart = useCallback((rowId) => {
    if (isSaving) {
      return;
    }
    setDragSourceId(rowId);
  }, [isSaving]);

  const handleDragEnd = useCallback(() => {
    setDragSourceId('');
  }, []);

  const handleDrop = useCallback(async (targetId) => {
    if (!dragSourceId || dragSourceId === targetId || isSaving) {
      setDragSourceId('');
      return;
    }

    const nextRows = reorderAgreementRows(rows, dragSourceId, targetId);
    setRows(nextRows);
    setDragSourceId('');
    await persistRows(nextRows, 'Order updated.');
  }, [dragSourceId, isSaving, persistRows, rows]);

  return {
    rows,
    newName,
    setNewName,
    isLoading,
    isSaving,
    error,
    success,
    deleteTargetId,
    dragSourceId,
    handleAdd,
    startEdit,
    cancelEdit,
    updateDraft,
    saveEdit,
    requestDeleteRow,
    cancelDeleteRow,
    confirmDeleteRow,
    handleDragStart,
    handleDragEnd,
    handleDrop,
  };
}
